import React, { useEffect, useState } from 'react';
import SubModelItem from './Components/SubModelItem';
import FieldItem from './Components/FieldItem';
import Button from 'Components/DesignSystem/Button/Button';
import Search from 'Components/DesignSystem/Search/Search';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { ALL, BOOLEAN, CATEGORY_NAME, CATEGORY_TYPE, CREATE_CATEGORY, CREATE_SUB_MODEL, MODEL_EXPLORER, DECIMAL, FRACTION, INTEGER, MULTI_CHOICE, PRODUCT_CATEGORY, SUB_MODEL, SUB_MODEL_NAME, SUB_MODEL_TYPE, FIELD, TEXT_FIELD, CONNECTOR, CONNECTOR_NAME, TRANSFORMATION, CREATE_CONNECTOR, EXPAND_ALL, COLLAPSE_ALL, RANGE_FIELD, EMPTY_MODEL_TITLE, EMPTY_MODEL_DESCRIPTION, SAVE_IN_PROGRESS_MESSAGE, CREATE, LOADING_DATA_MODEL_MESSAGE } from '../../ISRConstants';
import CreateISRDialog from '../../Components/CreateISRDialog';
import BooleanField from './Components/BooleanField';
import CreateFieldDialog from '../../Components/CreateFieldDialog';
import { TConnector, TField, TIndexState, TProductCategory, TSubmodel, TTransformation, TUpsertISRProjectDataModelsInput } from './model';
import SubModelDetails from './Components/SubModelDetails';
import TextField from './Components/TextField';
import IntegerField from './Components/IntegerField';
import DecimalField from './Components/DecimalField';
import MultiChoiceField from './Components/MultiChoiceField';
import EmptyState from 'Components/DesignSystem/EmptyState/EmptyState';
import PageSkeleton from 'Components/DesignSystem/PageSkeleton/PageSkeleton';
import NoDataIllustration from 'Components/DesignSystem/EmptyState/NoDataIllustration';
import CategoryField from './Components/CategoryField';
import FractionalField from './Components/FractionalField';
import ConnectorField from './Components/ConnectorField';
import TransformationField from './Components/TransformationField';
import ProductCategoryItem from './Components/ProductCategoryItem';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { useISRContext } from '../../Hooks/useISRContext';
import RangeField from "./Components/RangeField";

type TDefineModel = {
    id: string;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    setUserProjectDataModels: React.Dispatch<React.SetStateAction<TUpsertISRProjectDataModelsInput>>;
    createISRProject: () => void;
    /**
     * The FIRST load only — true while the project fetch is in flight and there is
     * nothing yet to draw. Deliberately not "any request is running": `showLoader` in
     * `ISRMainPage` is also true while saving, and collapsing this tab into a skeleton
     * on every autosave would throw the user's place away. The caller narrows it with
     * `!project?.id`; see design-system § 314.
     */
    loading?: boolean;
}


const DefineModel = ({ id, userProjectDataModels, setUserProjectDataModels, createISRProject, loading = false }: TDefineModel) => {
    const isrContext = useISRContext();
    const [searchInput, setSearchInput] = useState("");

    const [collapseAll, setCollapseAll] = useState<boolean>(false);
    const [selectedFilter] = useState<string>(ALL);

    const [index, setIndex] = useState<TIndexState>({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, });
    const [selectedItem, setSelectedItem] = useState<TField | TSubmodel | TProductCategory | TConnector | null>(null);
    const [subSelectedItem, setSubSelectedItem] = useState<TTransformation | null>(null);
    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [showCategoryDialog, setShowCategoryDialog] = useState<boolean>(false);
    const [showFieldDialog, setShowFieldDialog] = useState<boolean>(false);
    const [showConnectorDialog, setShowConnectorDialog] = useState<boolean>(false);

    useEffect(() => {
        const project = userProjectDataModels?.project;
        if (!project) return;

        // Re-select item inside a submodel
        if (index.dataModelIndex != null && index.dataModelIndex < project.dataModels.length) {
            const subModel = project.dataModels[index.dataModelIndex];
            if (index.categoryIndex != null && index.categoryIndex < subModel.data.productCategories.length) {
                setSelectedItem(subModel.data.productCategories[index.categoryIndex]);
            } else if (index.fieldIndex != null && index.fieldIndex < subModel.data.fields.length) {
                setSelectedItem(subModel.data.fields[index.fieldIndex]);
            } else if (selectedItem?.id !== subModel.id) {
                // Only update when the ID changed (e.g. newly created submodel received its server ID).
                // Skipping when IDs match prevents an infinite loop: SubModelDetails writes back to
                // userProjectDataModels via updateSubmodelInDataModels, which would re-trigger this
                // effect and keep replacing selectedItem with a new reference indefinitely.
                setSelectedItem(subModel);
            }
            return;
        }

        // Re-select standalone connector
        if (index.connectorOutsideDataModelIndex != null && index.connectorOutsideDataModelIndex < (project.connectors?.length ?? 0)) {
            setSelectedItem(project.connectors[index.connectorOutsideDataModelIndex]);
            return;
        }

        // Re-select standalone category
        if (index.categoryOutsideDataModelIndex != null && index.categoryOutsideDataModelIndex < project.productCategories.length) {
            setSelectedItem(project.productCategories[index.categoryOutsideDataModelIndex]);
            return;
        }

        // Re-select standalone field
        if (index.fieldOutsideDataModelIndex != null && index.fieldOutsideDataModelIndex < project.fields.length) {
            setSelectedItem(project.fields[index.fieldOutsideDataModelIndex].data);
            return;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProjectDataModels]);

    useEffect(() => {
        if (selectedItem !== null) return;
        const project = userProjectDataModels?.project;
        if (!project) return;
        if (project.dataModels?.length > 0) {
            setSelectedItem(project.dataModels[0]);
            setIndex({ dataModelIndex: 0, fieldIndex: null, fieldOutsideDataModelIndex: null });
        } else if (project.fields?.length > 0) {
            setSelectedItem(project.fields[0].data);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: 0, categoryOutsideDataModelIndex: 0 });
        } else if (project.productCategories?.length > 0) {
            setSelectedItem(project.productCategories[0]);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: 0 });
        } else if (project.connectors?.length > 0) {
            setSelectedItem(project.connectors[0]);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null, connectorOutsideDataModelIndex: 0 });
        }
        // selectedItem intentionally omitted: the guard `if (selectedItem !== null) return` reads
        // the current value via closure; adding it to deps would cause a circular re-run each time
        // this effect sets selectedItem, which is wasteful and masks stale-selection bugs.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProjectDataModels]);

    const handleSearchInputChange = (value: string) => {
        setSearchInput(value);
    };

    const openDialog = () => setShowDialog(true);
    const closeDialog = () => setShowDialog(false);

    const closeFieldDialog = () => setShowFieldDialog(false);
    const closeCategoryDialog = () => setShowCategoryDialog(false);
    const closeConnectorDialog = () => setShowConnectorDialog(false);

    const onFieldSubmit = (fieldName: string, fieldType: string, calculated: boolean, filter: boolean) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TField = { type: fieldType, name: fieldName, belongsToDataModel: false, dataConfiguration: { enableCalculation: calculated, filterAvailable: filter }, additionalData: {}, rules: [], };
        addField(newField);
    };

    const onConnectorFieldSubmit = (fieldName: string) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TConnector = { type: "Connector", data: { type: "Connector", isConsumable: true, name: fieldName, belongsToDataModel: false, submodels: [] } };
        addConnector(newField);
    };

    const addConnector = (newField: TConnector) => {
        const newIndex = userProjectDataModels.project.connectors?.length ?? 0;
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels) {
                return prevDataModels;
            }
            if (prevDataModels.project) {
                const updatedCategories = [
                    ...prevDataModels.project.connectors ?? [],
                    newField,
                ];
                return {
                    ...prevDataModels,
                    project: {
                        ...prevDataModels.project,
                        connectors: updatedCategories,
                    },
                };
            }
            return prevDataModels;
        });
        setSelectedItem(newField);
        setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null, connectorOutsideDataModelIndex: newIndex });
    };

    const onCategoryFieldSubmit = (fieldName: string) => {
        isrContext?.setUpdateLocalState(true);
        const newField: TProductCategory = { type: 'Category', data: { type: 'Category', name: fieldName, belongsToDataModel: false, categories: { type: '', data: [] }, transformations: [], compatioScore: false, required: false } };
        addCategory(newField);
    };

    const addCategory = (newField: TProductCategory) => {
        const newIndex = userProjectDataModels.project.productCategories.length;
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels) {
                return prevDataModels;
            }
            if (prevDataModels.project) {
                const updatedCategories = [
                    ...prevDataModels.project.productCategories,
                    newField,
                ];
                return {
                    ...prevDataModels,
                    project: {
                        ...prevDataModels.project,
                        productCategories: updatedCategories,
                    },
                };
            }
            return prevDataModels;
        });
        setSelectedItem(newField);
        setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: newIndex, categoryOutsideDataModelIndex: newIndex });
    };

    const removeConnector = (connectorId: string | null | undefined) => {
        if (!connectorId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const connectors = userProjectDataModels.project.connectors ?? [];
        const deletedIndex = connectors.findIndex(c => c?.id === connectorId);
        const newLength = connectors.length - 1;
        const newIndex = Math.max(0, Math.min(deletedIndex, newLength - 1));
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels || !prevDataModels.project) {
                return prevDataModels;
            }
            return {
                ...prevDataModels,
                project: {
                    ...prevDataModels.project,
                    connectors: prevDataModels.project.connectors.filter(c => c?.id !== connectorId),
                },
            };
        });
        if (newLength === 0) {
            setSelectedItem(null);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null, connectorOutsideDataModelIndex: null });
        } else {
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null, connectorOutsideDataModelIndex: newIndex });
        }
    };

    const removeCategory = (categoryId: string | null | undefined) => {
        if (!categoryId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const categories = userProjectDataModels.project.productCategories;
        const deletedIndex = categories.findIndex(c => c?.id === categoryId);
        const newLength = categories.length - 1;
        const newIndex = Math.max(0, Math.min(deletedIndex, newLength - 1));
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels || !prevDataModels.project) {
                return prevDataModels;
            }
            return {
                ...prevDataModels,
                project: {
                    ...prevDataModels.project,
                    productCategories: prevDataModels.project.productCategories.filter(c => c?.id !== categoryId),
                },
            };
        });
        if (newLength === 0) {
            setSelectedItem(null);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null });
        } else {
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: newIndex, categoryOutsideDataModelIndex: newIndex });
        }
    };

    const removeTransformation = (categoryId: string, transformationId: string) => {
        if (!categoryId || !transformationId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels || !prevDataModels.project) {
                return prevDataModels;
            }
            const updatedCategories = prevDataModels.project.productCategories.map(
                (category) => {
                    if (category.id === categoryId) {
                        category.data.transformations = category?.data?.transformations?.filter(
                            transformation =>
                                transformation.id !== transformationId)
                    }
                    return category
                }
            );
            return {
                ...prevDataModels,
                project: {
                    ...prevDataModels.project,
                    productCategories: updatedCategories,
                },
            };
        });
    };

    const addField = (newField: TField) => {
        const newIndex = userProjectDataModels.project.fields.length;
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels) {
                return prevDataModels;
            }

            if (prevDataModels.project) {
                return { ...prevDataModels, project: { ...prevDataModels.project, fields: [...prevDataModels.project.fields, { type: newField.type, data: newField }], }, };
            }

            return prevDataModels;
        });
        setSelectedItem(newField);
        setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: newIndex, categoryOutsideDataModelIndex: null });
    };

    const removeField = (fieldId: string | null | undefined) => {
        if (!fieldId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const fields = userProjectDataModels.project.fields;
        const deletedIndex = fields.findIndex(f => f?.id === fieldId);
        const newLength = fields.length - 1;
        const newIndex = Math.max(0, Math.min(deletedIndex, newLength - 1));
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels) {
                return prevDataModels;
            }
            if (prevDataModels.project) {
                return { ...prevDataModels, project: { ...prevDataModels.project, fields: prevDataModels.project.fields.filter(field => field?.id !== fieldId), }, };
            }
            return prevDataModels;
        });
        if (newLength === 0) {
            setSelectedItem(null);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null });
        } else {
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: newIndex, categoryOutsideDataModelIndex: null });
        }
    };

    const onSubmodelFieldSubmit = (submodelName: string): boolean => {
        if (userProjectDataModels.project.dataModels.filter(submodel => submodel.data.name.toLowerCase() === submodelName.toLowerCase()).length > 0) {
            toast.error("Submodel with same name exists");
            return false;
        }
        isrContext?.setUpdateLocalState(true);
        const newSubmodel: TSubmodel = { type: "Submodel", data: { name: submodelName, fields: [], productCategories: [], connectors: [], required: false, minInstantiation: 1, maxInstantiation: 1 } }
        addSubmodel(newSubmodel);
        return true;
    }

    const addSubmodel = (newSubmodel: TSubmodel) => {
        const newIndex = userProjectDataModels.project.dataModels.length;
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            const newDataModels = { ...prevDataModels };
            if (newDataModels.project) {
                newDataModels.project.dataModels.push(newSubmodel);
            }
            return newDataModels as TUpsertISRProjectDataModelsInput;
        });
        setSelectedItem(newSubmodel);
        setIndex({ dataModelIndex: newIndex, fieldIndex: null, fieldOutsideDataModelIndex: null });
    };

    const removeSubmodel = (subModelId: string | null | undefined) => {
        if (!subModelId) {
            toast.info(SAVE_IN_PROGRESS_MESSAGE);
            return;
        }
        const dataModels = userProjectDataModels.project.dataModels;
        if (dataModels.length <= 1) {
            toast.error('At least one submodel is required. You cannot delete the last submodel.');
            return;
        }
        const deletedIndex = dataModels.findIndex(s => s?.id === subModelId);
        const newLength = dataModels.length - 1;
        const newIndex = Math.max(0, Math.min(deletedIndex, newLength - 1));
        setUserProjectDataModels((prevDataModels: TUpsertISRProjectDataModelsInput) => {
            if (!prevDataModels) {
                return prevDataModels;
            }
            if (prevDataModels.project) {
                return { ...prevDataModels, project: { ...prevDataModels.project, dataModels: prevDataModels.project.dataModels.filter(submodel => submodel?.id !== subModelId), }, };
            }
            return prevDataModels;
        });
        if (newLength === 0) {
            setSelectedItem(null);
            setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null });
        } else {
            setIndex({ dataModelIndex: newIndex, fieldIndex: null, fieldOutsideDataModelIndex: null });
        }
    };

    const handleSubmodelSelect = (dataModelIndex: number) => {
        setIndex({ dataModelIndex, fieldIndex: null, fieldOutsideDataModelIndex: null });
    };

    const handleFieldOutsideDataModelSelect = (fieldOutsideDataModelIndex: number) => {
        setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: fieldOutsideDataModelIndex, categoryOutsideDataModelIndex: fieldOutsideDataModelIndex });
    };

    const handleConnectorsOutsideDataModelSelect = (fieldOutsideDataModelIndex: number) => {
        setIndex({ dataModelIndex: null, fieldIndex: null, fieldOutsideDataModelIndex: null, categoryOutsideDataModelIndex: null, connectorOutsideDataModelIndex: fieldOutsideDataModelIndex });
    };

    const handleCollapseAllClick = () => {
        setCollapseAll((prev) => prev = !prev);
    }

    const filteredFields = searchInput ?
        userProjectDataModels?.project?.fields
            .filter(field => {
                const search = searchInput.toLowerCase();
                return field.data.name.toLowerCase().includes(search) ||
                    (field.data.rules ?? []).some(rule =>
                        rule.name?.toLowerCase().includes(search)
                    );
            })
        : userProjectDataModels?.project?.fields;

    const filteredCategories = searchInput ?
        userProjectDataModels?.project?.productCategories
            .filter(category =>
                category.data.name.toLowerCase().includes(searchInput.toLowerCase())
            )
        : userProjectDataModels?.project?.productCategories;

    const filteredConnectors = searchInput ?
        userProjectDataModels?.project?.connectors
            .filter(connector =>
                connector.data.name.toLowerCase().includes(searchInput.toLowerCase())
            )
        : userProjectDataModels?.project?.connectors;


    /*
      The first-load state (design-system § 314). Until this landed the tab drew its
      full chrome over empty lists while the fetch ran — a live "Create Sub model"
      button above a tree that had not arrived, which reads as "loaded and empty"
      rather than "loading".

      An early return, AFTER every hook, so nothing above is skipped and no effect
      changes order. The dialogs below are unreachable at first load (they open from
      controls this branch does not render), so the branch loses nothing.

      The height class is the container's own — `explorer` fills what it is given, and
      matching it here is what stops the page jumping when the rows land.
    */
    if (loading) {
        return (
            <PageSkeleton
                preset="explorer"
                rows={6}
                label={LOADING_DATA_MODEL_MESSAGE}
                className="tw-h-[calc(100vh-171px)]"
                data-testid={`${id}-loading`}
            />
        );
    }

    return (
        <React.Fragment>
            <div id={id} className="tw-flex tw-h-[calc(100vh-171px)]">
                <div id={`${id}-sidebar`} className="tw-flex-grow tw-w-[30%] tw-max-h-full tw-py-2.5 tw-pe-2 tw-border-r tw-border-solid tw-border-edge tw-overflow-y-auto">
                    <div id={`${id}-sidebar-header`} className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-2'>
                        <Typography
                            variant="title-header"
                            as="h2"
                            id={`${id}-model-label`}
                            data-testid={`${id}-model-label`}
                            className='tw-text-ink'
                        >
                            {MODEL_EXPLORER}
                        </Typography>
                        {/* `sm` is 28px — the exact height this button already had.
                            The rail's ONE loud control (user call, 2026-09-07): the accent
                            OUTLINE it always had — a filled primary read too heavy here.
                            Everything else in the rail is quiet (adders plain `secondary`,
                            Collapse All `subtle`), so this outline + the selected row are
                            the only accent surfaces. Inlined, not a shared constant: the
                            old BLUE_OUTLINE seam is closed and this is its sole survivor. */}
                        <Button
                            id={`${id}-create-submodel-btn`}
                            data-testid={`${id}-create-submodel-btn`}
                            variant="secondary"
                            size="sm"
                            className="!tw-border-accent !tw-text-accent"
                            onClick={openDialog}
                        >
                            {CREATE} {SUB_MODEL}
                        </Button>
                        {/*<div id={`${id}-model-menu`}>*/}
                        {/*    <DataModelMenu onOpen={openDialog} openFieldDialog={openFieldDialog} openCategoryDialog={openCategoryDialog} openConnectorDialog={openConnectorDialog} />*/}
                        {/*</div>*/}
                    </div>
                    {/*<ModelFilter*/}
                    {/*    id={`${id}-model-filter`}*/}
                    {/*    labels={[ALL, SUB_MODEL, FIELD, PRODUCT_CATEGORY]}*/}
                    {/*    selectedFilter={selectedFilter}*/}
                    {/*    setSelectedFilter={(filter) => { setSearchInput(''); setSelectedFilter(filter); }}*/}
                    {/*/>*/}
                    <div id={`${id}-search-section`} className='tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mt-2'>
                        {/* No `label`: there is no room for one in this rail, and Search
                            falls back to naming itself from the placeholder — which is
                            more than the raw input had, since it carried no accessible
                            name at all. */}
                        <Search
                            id={`${id}-search-input`}
                            data-testid={`${id}-search-input`}
                            className='tw-flex-grow'
                            value={searchInput}
                            onChange={handleSearchInputChange}
                            placeholder="Search"
                        />
                        {/* A view utility, not an action — `subtle` ink since 2026-09-07
                            (it was a blue text button under the BLUE_OUTLINE deviation). */}
                        <Button
                            id={`${id}-collapse-btn`}
                            data-testid={`${id}-collapse-btn`}
                            variant="subtle"
                            size="md"
                            onClick={handleCollapseAllClick}
                        >
                            {collapseAll ? EXPAND_ALL : COLLAPSE_ALL}
                        </Button>
                    </div>

                    <ul id={`${id}-submodels-list`} aria-label={SUB_MODEL}>
                        {(selectedFilter === ALL || selectedFilter === SUB_MODEL) && userProjectDataModels?.project?.dataModels.length > 0 ? userProjectDataModels?.project?.dataModels?.map((submodel, submodelIndex) => {
                            const show = searchInput ? submodel.data.name.toLowerCase().includes(searchInput.toLowerCase()) ||
                                submodel.data.fields.filter(field =>
                                    field.name.toLowerCase().includes(searchInput.toLowerCase()) ||
                                    (field.rules ?? []).some(rule =>
                                        rule.name?.toLowerCase().includes(searchInput.toLowerCase())
                                    )
                                ).length > 0 ||
                                submodel.data.productCategories.filter(category =>
                                    category.data.name.toLowerCase().includes(searchInput.toLowerCase())
                                ).length > 0 ||
                                submodel.data.connectors.filter(connector =>
                                    connector.data.name.toLowerCase().includes(searchInput.toLowerCase())
                                ).length > 0 : true;
                            if (!show) {
                                // `return <></>` here produced React's "unique key prop"
                                // warning on every filtered render: a fragment inside a
                                // .map() is a child that cannot take the key.
                                return null;
                            }
                            return <li key={submodel?.id} id={`${id}-submodel-container-${submodelIndex}`}><SubModelItem
                                id={`${id}-submodel-${submodelIndex}`}
                                searchInput={searchInput}
                                submodel={submodel}
                                onDeleteSubmodel={removeSubmodel}
                                selectedItem={selectedItem}
                                collapseAll={collapseAll}
                                setSelectedItem={setSelectedItem}
                                classColor={submodel?.data?.class?.colour ?? ''}
                                setUserProjectDataModels={setUserProjectDataModels}
                                setIndex={setIndex}
                                indexInfo={index}
                                submodelIndex={submodelIndex}
                                userProjectDataModels={userProjectDataModels}
                                onSelect={() => {
                                    handleSubmodelSelect(submodelIndex);
                                    setSelectedItem(submodel);
                                }}
                                isSelected={(submodel.id === selectedItem?.id)}
                                setSubSelectedItem={setSubSelectedItem}
                            /></li>;
                        }
                        ) : null}
                    </ul>

                    <ul id={`${id}-fields-list`} aria-label={FIELD}>
                        {(selectedFilter === ALL || selectedFilter === FIELD) && filteredFields?.length > 0 ? filteredFields.map((field, fieldIndex) =>
                            <li id={`${id}-field-container-${fieldIndex}`} key={field?.id} className='tw-my-2'>
                                <FieldItem
                                    id={`${id}-field-${fieldIndex}`}
                                    fieldName={field?.data?.name}
                                    fieldId={field?.id}
                                    fieldType={field?.type}
                                    showCalculatedField={field?.data?.dataConfiguration?.enableCalculation}
                                    classColor={field?.data?.dataConfiguration?.class?.colour ?? ''}
                                    onDelete={removeField}
                                    indexInfo={index}
                                    userProjectDataModels={userProjectDataModels}
                                    selectedItem={selectedItem}
                                    onSelect={() => {
                                        handleFieldOutsideDataModelSelect(fieldIndex)
                                        setSelectedItem(field.data);
                                        setSubSelectedItem(null)
                                    }}
                                    isSelected={field.data === selectedItem}
                                />
                            </li>
                        ) : null}
                    </ul>

                    <ul id={`${id}-categories-list`} aria-label={PRODUCT_CATEGORY}>
                        {(selectedFilter === ALL || selectedFilter === PRODUCT_CATEGORY) && filteredCategories?.length > 0 ? filteredCategories.map((field, fieldIndex) => {
                            return (
                                <li id={`${id}-category-container-${fieldIndex}`} key={field?.id} className='tw-my-2'>
                                    <ProductCategoryItem
                                        id={`${id}-category-${fieldIndex}`}
                                        field={field}
                                        fieldName={field?.data?.name}
                                        fieldType={field?.type}
                                        userProjectDataModels={userProjectDataModels}
                                        selectedItem={selectedItem}
                                        indexInfo={index}
                                        onDelete={removeCategory}
                                        onSubDelete={removeTransformation}
                                        onSelect={() => {
                                            handleFieldOutsideDataModelSelect(fieldIndex);
                                            setSelectedItem(field);
                                        }}
                                        isSelected={field === selectedItem}
                                        setIndex={setIndex}
                                        setSubSelectedItem={setSubSelectedItem}
                                        setUserProjectDataModels={setUserProjectDataModels}
                                    />
                                </li>
                            );
                        }) : null}
                    </ul>

                    <ul id={`${id}-connectors-list`} aria-label={CONNECTOR}>
                        {userProjectDataModels?.project?.connectors && filteredConnectors.length > 0 ? filteredConnectors.map((field, fieldIndex) => {
                            return (
                                <li id={`${id}-connector-container-${fieldIndex}`} key={field?.id} className='tw-my-2'>
                                    <FieldItem
                                        id={`${id}-connector-${fieldIndex}`}
                                        fieldName={field?.data?.name}
                                        fieldId={field?.id}
                                        fieldType={field?.type}
                                        userProjectDataModels={userProjectDataModels}
                                        selectedItem={selectedItem}
                                        indexInfo={index}
                                        onDelete={removeConnector}
                                        onSelect={() => {
                                            handleConnectorsOutsideDataModelSelect(fieldIndex);
                                            setSelectedItem(field);
                                            setSubSelectedItem(null)
                                        }}
                                        isSelected={field === selectedItem}
                                    />
                                </li>
                            );
                        }) : null}
                    </ul>
                </div>

                <div id={`${id}-content`} className="tw-flex-grow tw-w-[70%] tw-max-h-full tw-py-4 tw-ps-4 tw-bg-surface-sunken tw-overflow-y-auto">
                    {selectedItem && selectedItem?.type === SUB_MODEL_TYPE ?
                        <SubModelDetails
                            id={`${id}-submodel-details`}
                            key={String(index.dataModelIndex ?? '')}
                            selectedItem={selectedItem}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            projectId={userProjectDataModels?.project?.id ?? ''}
                            hasFreestyleUIModel={userProjectDataModels?.has_freestyle_ui_model ?? false}
                        /> : null}
                    {selectedItem && (selectedItem?.type === TEXT_FIELD || selectedItem?.type === 'Field') ?
                        <TextField
                            id={`${id}-text-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === INTEGER ?
                        <IntegerField
                            id={`${id}-integer-field-details`}
                            key={selectedItem?.id ?? ''}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === DECIMAL ?
                        <DecimalField
                            id={`${id}-decimal-field-details`}
                            key={selectedItem?.id ?? ''}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === BOOLEAN ?
                        <BooleanField
                            id={`${id}-boolean-field-details`}
                            /* BooleanConfiguration seeds its dropdowns from `useState(selectedItem.dataConfiguration)`,
                               which only reads on mount — without a per-field key, switching boolean → boolean kept the
                               previous field's selections. Position is part of the key because a freshly created field
                               has no `id` until it is saved (two unsaved fields would otherwise share `''`); the `id`
                               covers a delete that slides a different field into the same position. */
                            key={`${selectedItem?.id ?? 'new'}:${index.dataModelIndex ?? ''}:${index.fieldIndex ?? ''}:${index.fieldOutsideDataModelIndex ?? ''}`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === MULTI_CHOICE ?
                        <MultiChoiceField
                            id={`${id}-multi-choice-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === RANGE_FIELD ?
                        <RangeField
                            id={`${id}-range-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {!subSelectedItem && selectedItem && selectedItem?.type === CATEGORY_TYPE ?
                        <CategoryField
                            id={`${id}-category-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            setSubSelectedItem={setSubSelectedItem}
                            setIndex={setIndex}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === FRACTION ?
                        <FractionalField
                            id={`${id}-fraction-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            createISRProject={createISRProject}
                        /> : null}
                    {selectedItem && selectedItem?.type === 'Connector' ?
                        <ConnectorField
                            id={`${id}-connector-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                        /> : null}
                    {subSelectedItem && subSelectedItem?.type === TRANSFORMATION &&
                        <TransformationField
                            id={`${id}-transformation-field-details`}
                            selectedItem={selectedItem}
                            userProjectDataModels={userProjectDataModels}
                            setUserProjectDataModels={setUserProjectDataModels}
                            indexInfo={index}
                            subSelectedItem={subSelectedItem}
                            createISRProject={createISRProject}
                        />}
                    {selectedItem === null && !!userProjectDataModels?.project?.id ?
                        /* DS EmptyState (2026-09-08) — the LAST consumer of `NoDataMessage`
                           leaves it, the same swap the project list and Relationships tab
                           made (okf/isr §§ 108/307). The old component was light-only —
                           a #fff card with #e4e4e8/#f4f4f7 ghost art and a slategray
                           label, none of it on the token seam — so it rendered as a white
                           slab in dark mode. Same id, testid added on the § pairing rule. */
                        <EmptyState
                            id={`${id}-no-data`}
                            data-testid={`${id}-no-data`}
                            illustration={<NoDataIllustration />}
                            title={EMPTY_MODEL_TITLE}
                            description={EMPTY_MODEL_DESCRIPTION}
                        /> : null}
                </div>
            </div>

            {showDialog ?
                <CreateISRDialog
                    id={`${id}-create-submodel-dialog`}
                    dialogHeader={CREATE_SUB_MODEL}
                    labelName={SUB_MODEL_NAME}
                    onClose={closeDialog}
                    onSubmodelFieldSubmit={onSubmodelFieldSubmit}
                    multiCreate
                    entityName="Submodel"
                /> : null}
            {showCategoryDialog ?
                <CreateISRDialog
                    id={`${id}-create-category-dialog`}
                    dialogHeader={CREATE_CATEGORY}
                    labelName={CATEGORY_NAME}
                    onClose={closeCategoryDialog}
                    onSubmodelFieldSubmit={onCategoryFieldSubmit}
                    multiCreate
                    entityName="Category"
                /> : null}
            {showConnectorDialog ?
                <CreateISRDialog
                    id={`${id}-create-connector-dialog`}
                    dialogHeader={CREATE_CONNECTOR}
                    labelName={CONNECTOR_NAME}
                    onClose={closeConnectorDialog}
                    onSubmodelFieldSubmit={onConnectorFieldSubmit}
                    multiCreate
                    entityName="Connector"
                /> : null}
            {showFieldDialog ?
                <CreateFieldDialog
                    id={`${id}-create-field-dialog`}
                    onClose={closeFieldDialog}
                    onFieldSubmit={onFieldSubmit}
                    multiCreate
                    entityName="Field"
                /> : null}
        </React.Fragment>
    );
}

export default DefineModel;
